import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { Session } from '../../api/session';
import { createInvite, getObject, getObjectAddresses, type ObjectInfo } from '../../api/tenant';
import { inviteDurations } from '../../domain/availability';
import { inviteMessage, safeInviteLink } from '../../domain/invite';
import { formatServerDate, startOfMoscowDay } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { passWindow, type PassDuration, type When } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips, Segmented } from '../../ui/Choice';
import { QrCode } from '../../ui/QrCode';
import { Skeleton } from '../../ui/Skeleton';
import { MessageScreen } from '../auth/screens';
import { LoadFailed } from '../common/LoadFailed';
import { UntilField } from '../common/UntilField';
import { DURATION_LABEL, windowCaption } from '../common/windowCaption';
import f from '../pass-form/passForm.module.css';
import s from './invite.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
}

const DURATION_CODE: Record<PassDuration, number> = { oneTime: 1, temporary: 2, permanent: 3 };
const WHEN_CHIPS = [
  { id: 'now' as const, label: 'Сейчас' },
  { id: 'today' as const, label: 'Сегодня' },
  { id: 'tomorrow' as const, label: 'Завтра' },
  { id: 'date' as const, label: 'Дата…' },
];

function Ready({ link, objectName, telegram }: { link: string; objectName: string; telegram: TelegramApi }) {
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Some Telegram views refuse the clipboard: select the text so the resident can copy it by hand.
      const el = document.getElementById('invite-link');
      if (el) window.getSelection()?.selectAllChildren(el);
    }
  };

  return (
    <main className={s.ready}>
      <h1 className={s.title}>Ссылка для гостя готова</h1>
      <p className={s.lead}>Гость откроет ссылку и сам впишет номер машины или имя. Пока он этого не сделал, пропуск в статусе «Ждём данные».</p>
      <p id="invite-link" className={s.link}>
        {link}
      </p>
      <div className={s.actions}>
        <button type="button" className={f.submit} onClick={() => telegram.share(link, inviteMessage(objectName))}>
          Отправить гостю в {telegram.name}
        </button>
        <button type="button" className={s.ghost} onClick={() => setShowQr((v) => !v)}>
          Показать QR — гость рядом
        </button>
        <button type="button" className={s.ghost} onClick={copy}>
          {copied ? 'Скопировано' : 'Скопировать ссылку'}
        </button>
      </div>
      {showQr && (
        <div className={s.qr}>
          <QrCode value={link} />
          <p className={s.lead}>Пусть гость наведёт камеру телефона на код.</p>
        </div>
      )}
    </main>
  );
}

/** «Гость впишет номер сам»: an invite without guest data (spec §6.4). */
export function InviteScreen({ session, telegram, object }: Props) {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);
  const durations = useMemo(() => inviteDurations(details.data?.settings ?? {}), [details.data]);

  const [addressId, setAddressId] = useState<number | null>(null);
  const [duration, setDuration] = useState<PassDuration>('oneTime');
  const [whenId, setWhenId] = useState<'now' | 'today' | 'tomorrow' | 'date'>('now');
  const [date, setDate] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  useEffect(() => {
    if (addressId === null && usable.length) setAddressId(usable[0].id);
  }, [usable, addressId]);
  useEffect(() => {
    if (durations.length && !durations.includes(duration)) setDuration(durations[0]);
  }, [durations, duration]);

  const now = new Date();
  const when: When = whenId === 'date' ? (date ? { date } : 'today') : whenId;
  const limit = details.data?.settings[duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime'];
  const limitHours = typeof limit === 'number' && limit > 0 ? limit : null;
  const window = passWindow({ when, duration, limitHours, now, until });

  const create = useMutation({
    mutationFn: () =>
      createInvite(session, {
        addressId: addressId!,
        durationType: DURATION_CODE[duration],
        ...(window.startsAt ? { startsAt: formatServerDate(window.startsAt) } : {}),
        ...(window.expiresAt ? { expiresAt: formatServerDate(window.expiresAt) } : {}),
      }),
    onSuccess: (invite) => {
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      const safe = safeInviteLink(invite.inviteLink);
      if (!safe) {
        // Never hand out a link we do not recognise: one with `chs` marks the guest as inside on some objects.
        telegram.notify('error');
        setError('Сервер вернул неожиданную ссылку. Приглашение создано, но отправлять его нельзя — напишите в поддержку.');
        return;
      }
      telegram.notify('success');
      setLink(safe);
    },
    onError: (e) => {
      telegram.notify('error');
      setError(describePassError(e).general);
    },
  });

  if (link) return <Ready link={link} objectName={object.name} telegram={telegram} />;
  if (addresses.isError || details.isError) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <main className={f.sheet} aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={56} />
      </main>
    );
  }
  if (!usable.length || !durations.length) {
    return <MessageScreen title="Ссылка недоступна" text="Приглашения на этом адресе выключены управляющей компанией. Обратитесь в УК." />;
  }

  return (
    <main className={f.sheet} aria-labelledby="invite-title">
      <header className={f.head}>
        <h1 id="invite-title" className={f.title}>
          Ссылка для гостя
        </h1>
        <p className={f.where}>Гость сам впишет номер машины или имя — вам не нужно их знать.</p>
      </header>

      {addresses.data.length > 1 && (
        <Chips
          label="Адрес"
          value={addressId}
          items={addresses.data.map((a) => ({ id: a.id, label: a.isBlocked ? `${a.name} — заблокирован` : a.name, disabled: a.isBlocked }))}
          onChange={(v) => v !== null && setAddressId(v)}
        />
      )}
      {durations.length > 1 && (
        <Segmented label="Срок пропуска" value={duration} items={durations.map((d) => ({ id: d, label: DURATION_LABEL[d] }))} onChange={setDuration} />
      )}
      {duration !== 'permanent' && (
        <div className={f.field}>
          <span className={f.label}>Когда</span>
          <Chips label="Когда" value={whenId} items={WHEN_CHIPS} onChange={(v) => v && setWhenId(v)} />
          {whenId === 'date' && (
            <input
              type="date"
              aria-label="Дата визита"
              className={f.input}
              value={date}
              min={formatServerDate(startOfMoscowDay(now)).slice(0, 10)}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </div>
      )}
      {duration === 'temporary' && (
        <UntilField value={until} onChange={setUntil} startsAt={window.startsAt} expiresAt={window.expiresAt} limitHours={limitHours} />
      )}
      <p className={f.caption}>{windowCaption(duration, window.startsAt, window.expiresAt, now)}</p>

      <div className={f.footer}>
        {error && (
          <p className={f.error} role="alert">
            {error}
          </p>
        )}
        <button
          type="button"
          className={f.submit}
          disabled={create.isPending}
          onClick={() => {
            if (whenId === 'date' && !date) return setError('Выберите дату визита');
            setError(null);
            create.mutate();
          }}
        >
          {create.isPending ? 'Создаём…' : 'Создать ссылку'}
        </button>
      </div>
    </main>
  );
}
